/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2026, Bulb Inc. For more information, please email contact@hellobulb.com.
 */

/* eslint-disable import/no-extraneous-dependencies */
import {
  LinkEditing, Plugin, type DowncastDispatcher, type ModelElement, type ViewElement,
} from 'ckeditor5';
/* eslint-enable import/no-extraneous-dependencies */
import BulbImageEditing from '../bulbimageediting';

type AutomaticDecorator = {
  mode: 'automatic';
  callback: (url: string) => boolean;
  attributes?: Record<string, string>;
  classes?: string | string[];
  styles?: Record<string, string>;
};

/**
 * Stores image links as `linkHref` and converts them to an anchor inside the
 * custom image figure. The stock LinkImageEditing plugin only supports imageBlock.
 */
export default class BulbLinkImageEditing extends Plugin {

  public static get pluginName(): string {
    return 'BulbLinkImageEditing' as const;
  }

  public static get requires() {
    return [BulbImageEditing, LinkEditing] as const;
  }

  public afterInit(): void {
    const { editor } = this;
    editor.model.schema.extend('image', { allowAttributes: ['linkHref'] });

    editor.conversion.for('dataDowncast').add((dispatcher) => this.registerDataLinkConverter(dispatcher));
    editor.conversion.for('editingDowncast').add((dispatcher) => {
      dispatcher.on('attribute:linkHref:image', (event, data, conversionApi) => {
        conversionApi.consumable.consume(data.item, event.name);
      }, { priority: 'high' });
    });

    this.listenTo(editor.model.document, 'change:data', () => {
      const imagesToRefresh = new Set<ModelElement>();

      editor.model.document.differ.getChanges()
        .forEach((change) => {
          if (change.type !== 'attribute' || change.attributeKey !== 'linkHref') {
            return;
          }

          Array.from(change.range.getItems())
            .filter((item) => item.is('element', 'image'))
            .forEach((image) => imagesToRefresh.add(image));
        });

      imagesToRefresh.forEach((image) => editor.editing.reconvertItem(image));
    });
  }

  private registerDataLinkConverter(dispatcher: DowncastDispatcher): void {
    dispatcher.on('attribute:linkHref:image', (event, data, conversionApi) => {
      if (!conversionApi.consumable.consume(data.item, event.name)) {
        return;
      }

      const figure = conversionApi.mapper.toViewElement(data.item);
      if (!figure) {
        return;
      }

      const { writer } = conversionApi;
      let anchor = BulbLinkImageEditing.findAnchor(figure);

      if (!data.attributeNewValue) {
        if (anchor) {
          Array.from(anchor.getChildren()).forEach((child) => {
            writer.move(writer.createRangeOn(child), writer.createPositionBefore(anchor));
          });
          writer.remove(anchor);
        }
        return;
      }

      if (!anchor) {
        anchor = writer.createContainerElement('a');
        writer.insert(writer.createPositionAt(figure, 0), anchor);
      }

      writer.setAttribute('href', data.attributeNewValue, anchor);
      this.applyAutomaticDecorators(anchor, data.attributeNewValue, writer);
    }, { priority: 'high' });
  }

  private static findAnchor(figure: ViewElement): ViewElement | undefined {
    return Array.from(figure.getChildren())
      .find((child) => child.is('element', 'a')) as ViewElement | undefined;
  }

  private applyAutomaticDecorators(anchor, url: string, writer): void {
    const configuredDecorators = this.editor.config.get('link.decorators') || {};
    const decorators = Object.values(configuredDecorators)
      .filter((decorator: AutomaticDecorator) => decorator.mode === 'automatic');

    (decorators as AutomaticDecorator[]).forEach((decorator) => {
      const shouldApply = decorator.callback(url);

      Object.entries(decorator.attributes || {}).forEach(([key, value]) => {
        if (shouldApply) {
          writer.setAttribute(key, value, anchor);
        } else {
          writer.removeAttribute(key, anchor);
        }
      });

      if (decorator.classes) {
        if (shouldApply) {
          writer.addClass(decorator.classes, anchor);
        } else {
          writer.removeClass(decorator.classes, anchor);
        }
      }

      Object.entries(decorator.styles || {}).forEach(([key, value]) => {
        if (shouldApply) {
          writer.setStyle(key, value, anchor);
        } else {
          writer.removeStyle(key, anchor);
        }
      });
    });
  }
}
